import { useNavigate } from 'react-router-dom'

import { Link, Accordion, Text, Button, Image, Heading } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectAnnualTaxDisqualificationReason,
  selectCurrentAnnualTaxFiling,
} from '../annualTaxFilings.selector'
import { AnnualTaxFilingDisqualifiedReason } from '../annualTaxFilings.slice'

const getDisqualificationMessage = (
  reason: AnnualTaxFilingDisqualifiedReason | null,
  taxYear: string
): string => {
  const defaultMessage = `Unfortunately, you missed the deadline for completing required tasks for your ${taxYear} tax filing.`

  if (!reason) {
    return defaultMessage
  }

  switch (reason) {
    case AnnualTaxFilingDisqualifiedReason.missed_tax_entity_verification_deadline:
      return `The deadline to verify your tax entity information for ${taxYear} has passed.`
    case AnnualTaxFilingDisqualifiedReason.missed_bookkeeping_tasks_deadline:
      return `The deadline to complete your bookkeeping tasks for ${taxYear} has passed.`
    case AnnualTaxFilingDisqualifiedReason.missed_books_locked_deadline:
      return `The deadline to lock your books for ${taxYear} has passed.`
    case AnnualTaxFilingDisqualifiedReason.missed_tax_questionnaire_deadline:
      return `The deadline to complete your tax questionnaire for ${taxYear} has passed.`
    case AnnualTaxFilingDisqualifiedReason.missed_1040_tax_return_extended_deadline:
    case AnnualTaxFilingDisqualifiedReason.missed_1120s_tax_return_extended_deadline:
      return `The extended filing deadline for your ${taxYear} tax return has passed.`
    default:
      return reason satisfies never
  }
}

const DisqualifiedSplashScreen = () => {
  const navigate = useNavigate()
  const disqualificationReason = useReselector(
    selectAnnualTaxDisqualificationReason
  )
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const annualTaxFiling = useReselector(selectCurrentAnnualTaxFiling)

  if (!annualTaxFiling?.disqualifiedAt) {
    // This shouldn't happen, but just in case
    return null
  }

  const message = getDisqualificationMessage(disqualificationReason, taxYear)
  const nextTaxYear = Number(taxYear) + 1

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex justify-center mb-8">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/annual_taxes.svg"
          className="w-[300px] h-[300px]"
          alt="Annual taxes"
        />
      </div>

      <div className="max-w-2xl mx-auto space-y-6 mb-12">
        <Heading className="text-3xl font-semibold">
          Important update about {taxYear} tax services
        </Heading>

        <Text>{message}</Text>
        <Text>
          We recommend connecting with a qualified local tax professional who
          can assist you with your {taxYear} filing.
        </Text>
        <Text>
          Please know that we&apos;re here to support you for the upcoming{' '}
          {nextTaxYear} tax season. We&apos;ll keep you informed about what to
          expect, and how we can help make the process smoother for you next
          year.
        </Text>
      </div>

      <div className="max-w-2xl mx-auto mb-12">
        <Text className="text-sm text-center mb-8">
          If you believe this is an error, please{' '}
          <Link href="https://support.joinheard.com/" newPage>
            contact support
          </Link>
          .
        </Text>

        <Accordion type="multiple" collapsible variant="text" className="mt-4">
          <Accordion.Item value="tax-documents">
            <Accordion.Trigger>
              <Text>Can I still access my tax documents?</Text>
            </Accordion.Trigger>
            <Accordion.Content>
              <Text>
                Yes, you can still access your tax documents from previous years
                in the Tax Return Archive.
              </Text>
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="tax-questionnaire">
            <Accordion.Trigger>
              <Text>
                Can I still access the answers to my tax questionnaire?
              </Text>
            </Accordion.Trigger>
            <Accordion.Content>
              <Text>
                No, tax questionnaire responses are only available during the
                active tax filing period.
              </Text>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>

      <div className="flex justify-center">
        <Button variant="secondary" onClick={() => navigate('/dashboard')}>
          Go to Home
        </Button>
      </div>
    </div>
  )
}

export default DisqualifiedSplashScreen
